import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useDebounceValue } from 'usehooks-ts';
import { ChevronDown, Search } from 'lucide-react';
import { DynamicIcon } from '@/components/Icon';
import aftermarketImg from '@assets/images/aftermarket.png';
import aftermarketExpressImg from '@assets/images/aftermarket-express.png';
import gtEngine from '@assets/images/gt-engine.png';
import amazonImg from '@assets/images/amazon.png';
import ebayImg from '@assets/images/ebay.png';
import walmartImg from '@assets/images/wallmart.png';
import zohoImg from '@assets/images/zoho.png';
import { OrderCard } from './OrderCard';
import type { OrderParams, Orders, ShippingType } from '@/apis/orders';
import { StatusOrders } from '@/apis/orders';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Empty } from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdminOrdersQuery } from '@/hooks/useAdminOrders';
import { DEFAULT_PAGE_SIZE } from '@/constants/pagination';

type ViewMode = 'grid' | 'list';

const STOREFRONT_TABS = [
  { label: 'ALL',                 key: 'ALL',                image: null,                  preserveColor: false },
  // { label: 'FAVOURITE',           key: 'FAVOURITE',          image: null,                  preserveColor: false },
  { label: 'G&T ENGINE PARTS',    key: 'GT_ENGINE_PARTS',    image: gtEngine,              preserveColor: false },
  { label: 'AFTERMARKET.EXPRESS', key: 'AFTERMARKET_EXPRESS',image: aftermarketExpressImg, preserveColor: true  },
  { label: 'AFTERMARKET.SUPPLY',  key: 'AFTERMARKET_SUPPLY', image: aftermarketImg,        preserveColor: false },
  { label: 'EBAY',                key: 'EBAY',               image: ebayImg,               preserveColor: false },
  { label: 'WALMART',             key: 'WALMART',            image: walmartImg,            preserveColor: false },
  { label: 'AMAZON',              key: 'AMAZON',             image: amazonImg,             preserveColor: false },
  { label: 'ZOHO',                key: 'ZOHO',               image: zohoImg,               preserveColor: true  },
] as const;

type StorefrontTabKey = (typeof STOREFRONT_TABS)[number]['key'];

/** Match order to the selected storefront tab (client-side) */
function matchesStorefrontTab(order: Orders, tab: StorefrontTabKey): boolean {
  if (tab === 'ALL') return true;
  const name = (order.storefront?.name ?? '').toUpperCase().replace(/[\s.]/g, '_');
  const marketplace = (order.marketplace ?? '').toUpperCase();
  return name.includes(tab) || marketplace === tab;
}

const STATUS_GROUP_OPTIONS = [
  { label: 'All Status Group',          value: undefined },
  { label: 'Pending',                   value: StatusOrders.PENDING },
  { label: 'Awaiting Payment',          value: StatusOrders.AWAITING_PAYMENT },
  { label: 'Processing',                value: StatusOrders.PROCESSING },
  { label: 'Paid',                      value: StatusOrders.PAID },
  { label: 'Shipped',                   value: StatusOrders.SHIPPED },
  { label: 'Completed',                 value: StatusOrders.COMPLETED },
  { label: 'Cancelled',                 value: StatusOrders.CANCELLED },
  { label: 'Cancellation Requested',    value: StatusOrders.CANCELLATION_REQUESTED },
  { label: 'Action Required',           value: StatusOrders.ACTION_REQUIRED_BY_CUSTOMER },
  { label: 'Request Document',          value: StatusOrders.REQUEST_DOCUMENT },
  { label: 'Contact Buyer',             value: StatusOrders.CONTACT_BUYER },
  { label: 'Doubled Order',             value: StatusOrders.DOUBLED_ORDER },
  { label: 'Return Request',            value: StatusOrders.RETURN_REQUEST },
  { label: 'Refunded',                  value: StatusOrders.REFUNDED },
  { label: 'Partial Refund',            value: StatusOrders.PARTIAL_REFUND },
  { label: 'Payment Action Request',    value: StatusOrders.PAYMENT_ACTION_REQUEST },
  { label: 'Stripe Pending',            value: StatusOrders.STRIPE_PENDING },
  { label: 'Paypal Pending',            value: StatusOrders.PAYPAL_PENDING },
  { label: 'NPA Pending',               value: StatusOrders.NPA_PENDING },
  { label: 'Stripe Completed',          value: StatusOrders.STRIPE_COMPLETED },
  { label: 'Paypal Completed',          value: StatusOrders.PAYPAL_COMPLETED },
  { label: 'NPA Completed',             value: StatusOrders.NPA_COMPLETED },
];

const SKELETON_ITEMS = Array.from({ length: 8 }, (_, i) => i);

export function OrderManagementPage() {
  const navigate = useNavigate({ from: '/order-management/' });
  const searchParams = useSearch({ from: '/_admin/order-management/' });

  const [partSearch, setPartSearch] = useState('');
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [searchInput, setSearchInput] = useState(searchParams.search ?? '');
  const [debouncedSearch] = useDebounceValue(searchInput, 400);
  const prevDebouncedSearch = useRef(debouncedSearch);

  const page        = searchParams.page ?? 1;
  const limit       = searchParams.limit ?? DEFAULT_PAGE_SIZE;
  const search      = searchParams.search ?? '';
  const statusFilter= (searchParams.status as StatusOrders | undefined) ?? undefined;
  const activeTab   = (searchParams.tab as StorefrontTabKey | undefined) ?? 'ALL';
  const view        = (searchParams.view as ViewMode | undefined) ?? 'list';

  const setParams = useCallback((updates: Record<string, unknown>) => {
    void navigate({ search: (prev) => ({ ...prev, ...updates }), replace: true });
  }, [navigate]);

  const statusLabel = STATUS_GROUP_OPTIONS.find((o) => o.value === statusFilter)?.label ?? 'All Status Group';

  const params: OrderParams = {
    orderNumber: search || undefined,
    status: statusFilter,
    page,
    limit,
  };

  const { data, isLoading } = useAdminOrdersQuery(params);
  const allOrders: Orders[] = data?.data ?? [];
  const meta = data?.meta;

  const orders = useMemo(
    () => allOrders.filter((o) => matchesStorefrontTab(o, activeTab)),
    [allOrders, activeTab]
  );

  useEffect(() => {
    if (debouncedSearch === prevDebouncedSearch.current) return;
    prevDebouncedSearch.current = debouncedSearch;
    setParams({ search: debouncedSearch || undefined, page: 1 });
  }, [debouncedSearch, setParams]);

  const handleStatusChange = (val: StatusOrders | undefined) => setParams({ status: val, page: 1 });
  const handleTabChange = (tab: StorefrontTabKey) => setParams({ tab, page: 1 });

  const handleShippingTypeChange = (_orderId: string, _type: ShippingType) => {
    // Status updates are handled inside each OrderListCard / OrderCard via useUpdateOrderStatus
  };

  return (
    <div className="flex flex-1 flex-col gap-4">
      {/* Page Header */}
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-bold text-black shrink-0">Order List</h1>

        {/* Search order */}
        <div className="relative flex min-w-28 flex-1 items-center rounded-full border border-smoke bg-neutral-white">
          <Search size={14} className="absolute left-3 text-silver" />
          <Input
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-9 w-full rounded-full pl-8 text-xs font-normal placeholder:text-xs placeholder:text-lightGray"
          />
        </div>

        {/* Search by part number */}
        {/* <div className="relative flex min-w-36 flex-1 items-center rounded-full border border-smoke bg-neutral-white">
          <Search size={14} className="absolute left-3 text-silver" />
          <Input
            placeholder="Search by Part Number"
            value={partSearch}
            onChange={(e) => setPartSearch(e.target.value)}
            className="h-9 w-full rounded-full pl-8 text-xs font-normal placeholder:text-xs placeholder:text-lightGray"
          />
        </div> */}

        {/* Status Group filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-smoke bg-neutral-white px-4 text-xs font-normal text-lightGray hover:bg-smoke1"
            >
              {statusLabel}
              <ChevronDown size={14} className="text-silver" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 p-1">
            <div className="max-h-80 overflow-y-auto">
              {STATUS_GROUP_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.label}
                  onClick={() => handleStatusChange(opt.value)}
                  className={`text-xs font-normal ${statusFilter === opt.value ? 'bg-primary-s50 text-primary-s500' : ''}`}
                >
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Right controls */}
        {/* <span className="hidden sm:inline text-base font-normal text-black whitespace-nowrap shrink-0">Unanswered Message</span> */}
        {/* <button
          type="button"
          onClick={() => setUnansweredOnly((v) => !v)}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${unansweredOnly ? 'bg-primary-s500' : 'bg-smoke'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-neutral-white shadow transition-transform ${unansweredOnly ? 'translate-x-6' : 'translate-x-1'}`} />
        </button> */}

        <div className="flex shrink-0 overflow-hidden rounded-full border border-smoke">
          <button
            type="button"
            onClick={() => setParams({ view: 'grid' })}
            className={`flex h-9 items-center gap-1.5 px-3 text-base font-normal transition-colors ${view === 'grid' ? 'bg-primary-s500 text-neutral-white' : 'bg-neutral-white text-lightGray hover:bg-smoke1'}`}
          >
            <DynamicIcon name="grid" size={14} />
            Grid
          </button>
          <button
            type="button"
            onClick={() => setParams({ view: 'list' })}
            className={`flex h-9 items-center gap-1.5 border-l border-smoke px-3 text-base font-normal transition-colors ${view === 'list' ? 'bg-primary-s500 text-neutral-white' : 'bg-neutral-white text-lightGray hover:bg-smoke1'}`}
          >
            <DynamicIcon name="list" size={14} />
            List
          </button>
        </div>
      </div>

      {/* Storefront / Marketplace filter tabs — list view only */}
      {view === 'list' && <div className="overflow-x-auto rounded-full">
        <div className="flex min-w-max rounded-full border border-smoke bg-neutral-white md:min-w-0 md:w-full">
          {STOREFRONT_TABS.map((tab, i) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabChange(tab.key)}
              className={[
                'shrink-0 flex items-center justify-center px-4 py-2 transition-colors whitespace-nowrap md:grow md:px-0',
                i === 0 ? 'rounded-l-full' : '',
                i === STOREFRONT_TABS.length - 1 ? 'rounded-r-full' : '',
                activeTab === tab.key
                  ? 'bg-primary-s500'
                  : 'hover:bg-smoke1',
                i > 0 && activeTab !== tab.key && activeTab !== STOREFRONT_TABS[i - 1].key
                  ? 'border-l border-smoke'
                  : '',
              ].join(' ')}
            >
              {tab.image ? (
                tab.preserveColor && activeTab === tab.key ? (
                  <span className="rounded bg-neutral-white px-1.5 py-0.5">
                    <img src={tab.image} alt={tab.label} className="h-5 w-auto max-w-[80px] object-contain" />
                  </span>
                ) : (
                  <img
                    src={tab.image}
                    alt={tab.label}
                    className={`h-6 w-auto max-w-[80px] object-contain ${activeTab === tab.key ? 'brightness-0 invert' : ''}`}
                  />
                )
              ) : (
                <span className={`text-base font-normal uppercase tracking-wide ${activeTab === tab.key ? 'text-neutral-white' : 'text-lightGray'}`}>
                  {tab.label}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>}

      {/* Content */}
      {isLoading ? (
        view === 'grid' ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {SKELETON_ITEMS.map((i) => (
              <Skeleton key={i} className="h-[520px] w-full rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {SKELETON_ITEMS.map((i) => (
              <Skeleton key={i} className="h-35 w-full rounded-2xl" />
            ))}
          </div>
        )
      ) : orders.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <Empty description={<span className="text-base font-normal text-lightGray">No orders found</span>} />
        </div>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {orders.map((order) => (
            <OrderCard
              key={order.id}
              view="grid"
              order={order}
              onShippingTypeChange={handleShippingTypeChange}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="flex min-w-max flex-col gap-3">
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                view="list"
                order={order}
                onShippingTypeChange={handleShippingTypeChange}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && meta && (meta.totalPages === undefined || meta.totalPages > 0) && (
        <Pagination
          page={page}
          totalPages={meta.totalPages}
          total={meta.total}
          limit={limit}
          hasNext={meta.hasNext}
          hasPrev={meta.hasPrev}
          onPageChange={(p) => setParams({ page: p })}
          onLimitChange={(newLimit) => setParams({ limit: newLimit, page: 1 })}
        />
      )}
    </div>
  );
}
